import React from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'
import { withRouter } from 'next/router'
import clsx from 'clsx'
import authDuck from 'components/auth/duck'
import Loader from 'components/loader'
import useAddToBag from 'components/shared/useRedux/useAddToBag'
import wishlistDuck from 'components/wishlist/duck'
import {
  segmentSignInViewed,
  segmentTrackProductSoldOutNotifyMe,
  segmentTrackProductComingSoonNotifyMe,
} from 'lib/segment'
import { SEGMENT_STORAGE_KEY_PROMPT_LOCATION } from 'lib/segment/const'
import Typo from 'constants/typography'
import ProductApiHandler from 'lib/api/product'
import StateStorage from 'lib/state-storage'
import { hasLocizeTranslation } from 'lib/utils/locize'
import styles from './styles.scss'
import AddToBagModalItem from './item'

class BaseAddToBagModal extends React.Component {
  state = {
    fullProductDetails: undefined,
    isLoading: true,
    productLoadFailed: false,
    waitlistFailed: false,
  }

  componentDidMount() {
    const { productDetails, productId } = this.props

    if (productDetails) {
      this.setState({ fullProductDetails: productDetails, isLoading: false })
      return
    }

    if (productId) {
      this.fetchProduct()
    }
  }

  componentWillUnmount() {
    this.productLoadCancelled = true
  }

  async fetchProduct() {
    const { productId } = this.props
    if (this.productFetchPending) return
    this.productFetchPending = true
    this.setState({ isLoading: true, productLoadFailed: false })
    try {
      const res = await ProductApiHandler.getProduct({
        token: null,
        product_id: productId,
        isGuestMode: StateStorage.isGuestMode(),
      })
      if (!this.productLoadCancelled) {
        const validProduct = Array.isArray(res?.attributes)
        this.setState({
          fullProductDetails: validProduct ? res : undefined,
          isLoading: false,
          productLoadFailed: !validProduct,
        })
      }
    } catch (error) {
      if (!this.productLoadCancelled) {
        this.setState({ isLoading: false, productLoadFailed: true })
      }
    } finally {
      this.productFetchPending = false
    }
  }

  addToBag = (sizeData, onAdded, afterSizeSave = false) => {
    const {
      close,
      onAddToBagError,
      onAddToBagSuccess,
      useAddToBag: dispatchUseAddToBag,
      enableInterstitial,
      enableSegment,
      router,
    } = this.props
    const { fullProductDetails, isLoading } = this.state

    if (isLoading && !afterSizeSave) {
      return
    }

    this.setState({ isLoading: true })

    let refData = null
    if (router.pathname === '/wishlist') {
      refData = {
        ref_id: fullProductDetails.id_product,
        ref_type: 'wishlist',
      }
    }

    dispatchUseAddToBag({
      enableInterstitial,
      enableSegment,
      product: fullProductDetails,
      returnParsedError: true,
      sizeData,
      onError: onAddToBagError,
      onSuccess: onAdded
        ? (result, context) => {
            onAddToBagSuccess?.(result, context)
            onAdded()
          }
        : onAddToBagSuccess,
      onFinish: close,
      refData,
    })
  }

  addToWaitlist = async (e, sizeData, setLocalWaitlisted) => {
    if (e) {
      e?.preventDefault()
    }

    const {
      close,
      productDetails,
      productId,
      source,
      token,
      updateShowLogin,
      onAddToWaitlistSuccess,
      ref_id,
      ref_type,
    } = this.props
    const { fullProductDetails, isLoading } = this.state
    const isComingSoon = fullProductDetails?.is_coming_soon
    const segmentData = {
      product: fullProductDetails,
      sizeData,
    }
    const refData = { ref_id, ref_type }

    if (isLoading) {
      return
    }

    if (!token) {
      close()
      // TODO: map it with a static object
      const prompt_location = source || 'notify_me_shop_the_look'
      segmentSignInViewed(prompt_location)
      sessionStorage.setItem(
        SEGMENT_STORAGE_KEY_PROMPT_LOCATION,
        prompt_location,
      )

      if (isComingSoon) {
        segmentTrackProductComingSoonNotifyMe(segmentData, refData, false)
      } else {
        segmentTrackProductSoldOutNotifyMe(segmentData, refData, false)
      }

      updateShowLogin(true)
      return
    }

    try {
      this.setState({ isLoading: true, waitlistFailed: false })

      const res = await ProductApiHandler.addToWaitList({
        product_id: productId || productDetails?.id_product,
        product_attribute_id: sizeData.id_product_attribute,
      })

      setLocalWaitlisted(true)
      if (isComingSoon) {
        segmentTrackProductComingSoonNotifyMe(segmentData, refData, true)
      } else {
        segmentTrackProductSoldOutNotifyMe(segmentData, refData, true)
      }
      this.setState({ isLoading: false, waitlistFailed: false })

      if (onAddToWaitlistSuccess) {
        onAddToWaitlistSuccess(res)
      }
    } catch (err) {
      if (isComingSoon) {
        segmentTrackProductComingSoonNotifyMe(segmentData, refData, false)
      } else {
        segmentTrackProductSoldOutNotifyMe(segmentData, refData, false)
      }
      this.setState({ isLoading: false, waitlistFailed: true })
    }
  }

  onSizeClick = async (e, sizeData) => {
    const {
      handleDeleteWishlist,
      invalidateWishlist,
      isWishlistContext,
      shouldMoveToBagAfterSelectedSize,
      updateWishlist,
      wishlistId,
      productId,
      onAddToBagError,
    } = this.props
    const { isLoading } = this.state

    e?.preventDefault()

    if (isLoading) return

    if (isWishlistContext) {
      this.setState({ isLoading: true })

      invalidateWishlist()
      try {
        await updateWishlist({
          id_wishlist_item: wishlistId,
          id_product: productId,
          id_product_attribute: sizeData.id_product_attribute,
        })
        // Saving the wishlist can unmount this modal. Do not depend on a
        // subsequent React state callback to dispatch the already-requested add.
        if (shouldMoveToBagAfterSelectedSize) {
          this.addToBag(sizeData, handleDeleteWishlist, true)
        } else {
          this.setState({ isLoading: false })
        }
      } catch (error) {
        this.setState({ isLoading: false })
        onAddToBagError?.(error)
      }
    } else {
      this.addToBag(sizeData)
    }
  }

  renderItem = (item, isComingSoon) => {
    const { t, selectedProductAttributeId } = this.props
    const isProductAttributeSelected =
      item.id_product_attribute === selectedProductAttributeId

    return (
      <AddToBagModalItem
        isComingSoon={isComingSoon}
        addToBag={this.onSizeClick}
        addToWaitlist={this.addToWaitlist}
        item={item}
        key={item.id_product_attribute}
        selected={isProductAttributeSelected}
        t={t}
      />
    )
  }

  render() {
    const { t, i18n } = this.props
    const { fullProductDetails, isLoading, productLoadFailed, waitlistFailed } =
      this.state
    const thai = i18n?.language?.split('-')[0] === 'th'
    const isComingSoon = fullProductDetails?.is_coming_soon

    return (
      <React.Fragment>
        <style jsx>{styles}</style>
        <section className="atb-modal__wrapper">
          <span className={clsx(Typo.subtitle1, 'atb-modal__title')}>
            {t('Choose your size')}
          </span>
          <hr className="atb-modal__line" />
          {waitlistFailed && (
            <p role="alert" className="atb-modal__request-error">
              {hasLocizeTranslation(
                t,
                'FALL_BACK_MODAL_TITLE',
                'Oops! Something went wrong',
              )}
            </p>
          )}
          {productLoadFailed && (
            <div className="atb-modal__recovery">
              <p role="status" id="size-picker-load-error">
                {hasLocizeTranslation(
                  t,
                  'SIZE_PICKER_LOAD_FAILED',
                  thai
                    ? 'ไม่สามารถโหลดขนาดสินค้าได้ กรุณาลองอีกครั้ง'
                    : 'We couldn’t load the sizes. Please try again.',
                )}
              </p>
              <button
                type="button"
                aria-describedby="size-picker-load-error"
                onClick={() => this.fetchProduct()}
              >
                {hasLocizeTranslation(
                  t,
                  'SIZE_PICKER_RETRY',
                  thai ? 'ลองอีกครั้ง' : 'Retry',
                )}
              </button>
            </div>
          )}
          {isLoading && (
            <div className="atb-modal__loader">
              <Loader />
            </div>
          )}
          {fullProductDetails?.attributes?.map((item) =>
            this.renderItem(item, isComingSoon),
          )}
        </section>
      </React.Fragment>
    )
  }
}

BaseAddToBagModal.defaultProps = {
  i18n: undefined,
  enableInterstitial: undefined,
  enableSegment: true,
  handleDeleteWishlist: undefined,
  onAddToBagError: undefined,
  onAddToBagSuccess: undefined,
  onAddToWaitlistSuccess: undefined,
  productDetails: undefined,
  productId: undefined,
  source: undefined,
  token: undefined,
  isWishlistContext: false,
  selectedProductAttributeId: undefined,
  shouldMoveToBagAfterSelectedSize: false,
  ref_id: undefined,
  ref_type: undefined,
  wishlistId: null,
}

BaseAddToBagModal.propTypes = {
  i18n: PropTypes.shape({ language: PropTypes.string }),
  close: PropTypes.func.isRequired,
  enableInterstitial: PropTypes.bool,
  enableSegment: PropTypes.bool,
  internationalization: PropTypes.shape({ shop: PropTypes.number }).isRequired,
  invalidateWishlist: PropTypes.func.isRequired,
  handleDeleteWishlist: PropTypes.func,
  onAddToBagError: PropTypes.func,
  onAddToBagSuccess: PropTypes.func,
  onAddToWaitlistSuccess: PropTypes.func,
  productDetails: PropTypes.shape({
    attributes: PropTypes.arrayOf(PropTypes.shape({})),
    id_product: PropTypes.number,
    is_coming_soon: PropTypes.bool,
  }),
  productId: PropTypes.number,
  isWishlistContext: PropTypes.bool,
  shouldMoveToBagAfterSelectedSize: PropTypes.bool,
  source: PropTypes.string,
  t: PropTypes.func.isRequired,
  token: PropTypes.string,
  updateShowLogin: PropTypes.func.isRequired,
  updateWishlist: PropTypes.func.isRequired,
  useAddToBag: PropTypes.func.isRequired,
  selectedProductAttributeId: PropTypes.number,
  ref_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ref_type: PropTypes.string,
  router: PropTypes.shape({ pathname: PropTypes.string }).isRequired,
  wishlistId: PropTypes.number,
}

/**
 * @param {Boolean} param0.enableInterstitial Flag to enable trigger cart notice when add to bag
 * @param {Boolean} param0.enableSegment Flag to trigger segment tracking when add to bag
 * @param {Function} param0.close Function to close the modal.
 * @param {Function} param0.onAddToWaitlistSuccess Function to trigger after the adding to waitlist success.
 * @param {Function} param0.onAddToBagError Function to trigger after the adding to bag fail.
 * @param {Function} param0.onAddToBagSuccess Function to trigger after the adding to bag success.
 * @param {Object} param0.productDetails Full product details, leave this empty to auto fetch full details with `productId` prop.
 * @param {Number} param0.productId Required if `productDetails` prop is not passed in, use to fetch full details.
 * @param {String} param0.source Use for Segment tracking, you'll know if you need it.
 * @param {Number} param0.selectedProductAttributeId product attribute item will be checked if it is selected product attribute id.
 */
const AddToBagModal = withRouter(
  connect(
    (state) => ({
      internationalization: state.internationalization,
      token: state.auth.token,
    }),
    (dispatch) =>
      bindActionCreators(
        {
          updateWishlist: (option) =>
            wishlistDuck.creators.get({ ...option, type: 'UPDATE' }),
          invalidateWishlist: wishlistDuck.creators.invalidate,
          updateShowLogin: authDuck.creators.updateShowLogin,
          useAddToBag,
        },
        dispatch,
      ),
  )(translate('global')(BaseAddToBagModal)),
)

export default AddToBagModal
